import { Button, Card, Form, Input, Message, Popconfirm, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useEffect, useState } from "react";
import { BOMAPI } from "@/apis/manufacturing";
import { IconDelete, IconEdit, IconCheck, IconPlus } from "@arco-design/web-react/icon";
import { Link, useNavigate } from "react-router-dom";
import { BillOfMaterialAdd } from "./bom-add";
import { BillOfMaterialInfo } from "./bom-info";
import { BillOfMaterialEdit } from "./bom-edit";
import { BomStatusDef, BomStatus } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";

const FormItem = Form.Item;
const Option = Select.Option;

export const BillOfMaterial = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [queryParams, setQueryParams] = useState({ status: BomStatus.Active });

    const pagination = { total, current: page, pageSize: 20 };

    const getBOMs = useCallback(async () => {
        setLoading(true);
        try {
            const values = await BOMAPI.query({ page, ...queryParams });
            setData(values.records);
            setTotal(values.total);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [page, queryParams]);

    useEffect(() => {
        getBOMs();
    }, [getBOMs]);

    const onFieldChange = (field, value) => {
        const values = form.getFieldsValue();
        values[field] = value;
        if (values.status === 0) {
            delete values.status;
        }
        setQueryParams(values);
        setPage(1);
    };

    const handleEnable = async (id) => {
        try {
            await BOMAPI.activate(id);
            Message.success("BOM enabled successfully");
            getBOMs();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleDisable = async (id) => {
        try {
            await BOMAPI.deactivate(id);
            Message.success("BOM disabled successfully");
            getBOMs();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const columns = [
        {
            title: "BOM Name",
            dataIndex: "bomName",
            render: (bomName, record) => (
                <Link to={`/workspace/manufacturing/bom/${record.id}`}>{bomName}</Link>
            ),
        },
        {
            title: "Finished Item Name",
            dataIndex: "finishedItems",
            render: (finishedItems) => (
                <div>
                    {(finishedItems || []).map((item, index) => (
                        <div key={item.id || index}>{item.itemName}</div>
                    ))}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (status) => <EnumLabel enumObj={BomStatusDef} value={status} />
        },
        {
            title: "Action",
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button type="text" size="small" icon={<IconEdit />} onClick={() => {
                        navigate(`/workspace/manufacturing/bom/${record.id}/edit`);
                    }}>
                        Edit
                    </Button>
                    {record.status === 2 ? (
                        <Popconfirm
                            title="Are you sure you want to enable this BOM?"
                            onOk={() => handleEnable(record.id)}
                        >
                            <Button type="text" size="small" icon={<IconCheck />} style={{ color: "green" }}>
                                Enable
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Are you sure you want to disable this BOM?"
                            onOk={() => handleDisable(record.id)}
                        >
                            <Button type="text" size="small" icon={<IconDelete />} status="danger">
                                Disable
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Card title="Bill of Material" extra={
            <Space size="large">
                <Form form={form} layout="inline" initialValues={{ status: BomStatus.Active }}>
                    <FormItem field="bomName" style={{ marginBottom: 0, marginRight: 8 }}>
                        <Input placeholder="BOM Name" allowClear onChange={(val) => onFieldChange("bomName", val)} style={{ width: 160 }} />
                    </FormItem>
                    <FormItem field="itemName" style={{ marginBottom: 0, marginRight: 8 }}>
                        <Input placeholder="Finished Item Name" allowClear onChange={(val) => onFieldChange("itemName", val)} style={{ width: 160 }} />
                    </FormItem>
                    <FormItem field="status" style={{ marginBottom: 0, marginRight: 8 }}>
                        <Select placeholder="Status" allowClear onChange={(val) => onFieldChange("status", val)} style={{ width: 120 }}>
                            <Option value={0}>All</Option>
                            {Object.entries(BomStatusDef).map(([, item]) => (
                                <Option key={item.value} value={item.value}>{item.label}</Option>
                            ))}
                        </Select>
                    </FormItem>
                </Form>
                <Link to="/workspace/manufacturing/bom/add">
                    <Button type="primary" icon={<IconPlus />}>Add BOM</Button>
                </Link>
            </Space>
        }>
            <Table
                rowKey="id"
                columns={columns}
                data={data}
                loading={loading}
                pagination={pagination}
                onChange={(pagination) => setPage(pagination.current)}
            />
        </Card>
    );
};

BillOfMaterial.Add = BillOfMaterialAdd;
BillOfMaterial.Info = BillOfMaterialInfo;
BillOfMaterial.Edit = BillOfMaterialEdit;

export * from "./bom-add";
export * from "./bom-info";
export * from "./bom-edit";
